import io from 'socket.io-client';

const REFRESH_MS = 400;

/**
 * Presence-only socket for the /spaces page.
 * Connects, asks which friends are online, and refreshes when someone joins or leaves.
 */
export default class SpacesSocket {
  constructor(config) {
    this.config = config || {};
    this.socket = null;
    this.ready = false;
    this.friendIds = [];
    this.onlineUsers = [];
    this.listeners = new Set();
    this.statusListeners = new Set();
    this.messageListeners = new Set();
    this.refreshTimer = null;
  }

  connect() {
    if (!this.config.socketUrl || this.socket) {
      return;
    }

    const query = {
      ...this.config,
      currentSceneName: 'spaces',
      currentSceneId: 0,
      creatorUserIdOfWorld: 0,
      url: window.location.href,
    };

    this.socket = io(query.socketUrl, {
      transports: ['websocket', 'polling'],
      query,
    });

    this.socket.on('initialized', () => {
      this.setReady(true);
      this.socket.emit('client_ready');
      this.requestFriendsInfo();
    });

    this.socket.on('disconnect', (reason) => {
      this.setReady(false);
      if (reason === 'io server disconnect') {
        setTimeout(() => {
          if (this.socket) {
            this.socket.connect();
          }
        }, 5000);
      }
    });

    this.socket.on('message', (payload) => {
      this.onMessage(payload);
    });
  }

  onMessage(payload) {
    this.messageListeners.forEach((listener) => listener(payload));
    if (!payload || !payload.eventName) {
      return;
    }

    switch (payload.eventName) {
      case 'getUsersInfo':
        this.onlineUsers = normalizeUsers(payload.users);
        this.notify();
        break;
      case 'sessionConnected_global':
      case 'sessionDisconnected_global':
        this.scheduleRefresh();
        break;
      default:
        break;
    }
  }

  setFriendIds(ids) {
    this.friendIds = (ids || [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
    this.requestFriendsInfo();
  }

  requestFriendsInfo() {
    if (!this.ready || !this.socket || !this.friendIds.length) {
      return;
    }
    this.socket.emit('message', {
      eventName: 'getUsersInfo',
      userIds: this.friendIds,
      senderId: this.config.userId,
    });
  }

  scheduleRefresh() {
    clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => {
      this.requestFriendsInfo();
    }, REFRESH_MS);
  }

  onChange(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onAnyMessage(listener) {
    this.messageListeners.add(listener);
    return () => this.messageListeners.delete(listener);
  }

  send(payload) {
    if (this.socket && payload) {
      this.socket.emit('message', payload);
    }
  }

  getIo() {
    return this.socket;
  }

  onStatus(listener) {
    this.statusListeners.add(listener);
    listener(this.ready);
    return () => this.statusListeners.delete(listener);
  }

  setReady(ready) {
    this.ready = ready;
    this.statusListeners.forEach((listener) => {
      listener(ready);
    });
  }

  notify() {
    this.listeners.forEach((listener) => {
      listener(this.onlineUsers);
    });
  }

  disconnect() {
    clearTimeout(this.refreshTimer);
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.setReady(false);
  }
}

function normalizeUsers(users) {
  if (Array.isArray(users)) {
    return users;
  }
  if (users && typeof users === 'object') {
    return Object.values(users);
  }
  return [];
}
